// 
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Automatically adds a configured "free gift" product to the cart once the
 * cart subtotal reaches a threshold, and removes it again if the subtotal
 * drops back below. Expects the gift product's own price to already be
 * $0.00 - this does not apply a discount, it just manages cart membership.
 *
 * Listens on the same theme-wide `StandardEvents.cartLinesUpdate` event every
 * other cart-aware component uses (fired from product forms, quick-add, the
 * cart drawer, the cart page, etc.), so it reacts correctly no matter where a
 * cart change originated. Re-syncs on connect too, so a stale gift line left
 * over from a previous session (e.g. subtotal dropped between visits) gets
 * cleaned up on next page load.
 */
class FreeGiftWithPurchase extends HTMLElement {
  /** Guards against reacting to the cart-update event this element's own add/remove dispatches. */
  #isSyncing = false;

  connectedCallback() {
    this.giftVariantId = this.dataset.variantId;
    this.thresholdCents = Number.parseInt(this.dataset.thresholdCents ?? '', 10);

    if (!this.giftVariantId || Number.isNaN(this.thresholdCents)) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#syncGiftItem();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = async (event) => {
    if (this.#isSyncing) return;

    try {
      await event.promise;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      // The triggering action itself may have failed (e.g. an add-to-cart error) -
      // still worth re-checking cart state in case it partially applied.
    }

    this.#syncGiftItem();
  };

  async #syncGiftItem() {
    /** @type {{items: Array<{variant_id: number, key: string, final_line_price: number}>}} */
    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());

    const giftLine = cart.items.find((item) => String(item.variant_id) === String(this.giftVariantId));

    // Excludes the gift line's own price from the qualifying total (it should
    // always be $0 anyway) so the gift item's own presence can never influence
    // whether it stays in the cart - avoids any self-referential feedback loop.
    const qualifyingTotal = cart.items
      .filter((item) => String(item.variant_id) !== String(this.giftVariantId))
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const shouldHaveGift = qualifyingTotal >= this.thresholdCents;

    if (shouldHaveGift && !giftLine) {
      await this.#addGift();
    } else if (!shouldHaveGift && giftLine) {
      await this.#removeGift(giftLine.key);
    }
  }

  async #addGift() {
    this.#isSyncing = true;
    try {
      await this.#request(Theme.routes.cart_add_url, {
        id: this.giftVariantId,
        quantity: 1,
        sections: this.#sectionIds(),
        sections_url: window.location.pathname,
      });
    } catch (error) {
      console.warn('[free-gift-with-purchase] Failed to add gift:', error);
    } finally {
      this.#isSyncing = false;
    }
  }

  /** @param {string} lineKey */
  async #removeGift(lineKey) {
    this.#isSyncing = true;
    try {
      await this.#request(Theme.routes.cart_change_url, {
        id: lineKey,
        quantity: 0,
        sections: this.#sectionIds(),
        sections_url: window.location.pathname,
      });
    } catch (error) {
      console.warn('[free-gift-with-purchase] Failed to remove gift:', error);
    } finally {
      this.#isSyncing = false;
    }
  }

  /**
   * Every `cart-items-component` on the page (cart drawer, cart page) needs its
   * section re-rendered so the gift line actually shows up/disappears visually -
   * same section-collection pattern `product-form.js` uses for a normal add.
   * @returns {string}
   */
  #sectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionIds).join(',');
  }

  /**
   * @param {string} url
   * @param {{id: string, quantity: number, sections: string, sections_url: string}} body
   */
  async #request(url, body) {
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: body.quantity === 0 ? 'remove' : 'add',
        context: 'free-gift-with-purchase',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${this.giftVariantId}`, quantity: body.quantity }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      }).then((r) => r.json());

      if (response.status) {
        throw new Error(response.message || 'Cart update failed');
      }

      const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'free-gift-with-purchase',
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      throw error;
    }
  }
}

if (!customElements.get('free-gift-with-purchase')) {
  customElements.define('free-gift-with-purchase', FreeGiftWithPurchase);
}
